import { c as _c } from "react/compiler-runtime";
import { useNetworkState } from '@react-hookz/web';
import { createColumnHelper } from '@tanstack/react-table';
import { useAtomCallback } from 'jotai/utils';
import { Fragment } from 'react';
import { toast } from 'sonner';
import type { TrackedWord } from '@/app/[locale]/(vocabulary)/_lib/LexiconTrie';
import type { VocabularySourceState } from '@/app/[locale]/(vocabulary)/_lib/vocab';
import { userWordPhaseMutationAtom } from '@/app/[locale]/(vocabulary)/_api';
import { VocabularyWordAction } from '@/app/[locale]/(vocabulary)/_components/cells';
import { VocabToggle } from '@/app/[locale]/(vocabulary)/_components/toggle-button';
import { userAtom } from '@/atoms/auth';
import { LoginToast } from '@/components/login-toast';
import { SortIcon } from '@/components/my-icon/sort-icon';
import { Div } from '@/components/ui/html-elements';
import { Separator } from '@/components/ui/separator';
import { HeaderTitle, TableDataCell, TableHeaderCell } from '@/components/ui/table-element';
import { filterFn } from '@/lib/table-utils';
import { useI18n } from '@/locales/client';
export function useVocabularyCommonColumns(rootRef) {
  const $ = _c(20);
  const t = useI18n();
  const columnHelper = createColumnHelper();
  const {
    online: isOnline
  } = useNetworkState();
  let t0;
  if ($[0] !== isOnline) {
    t0 = (get, _, vocab) => {
      if (!get(userAtom)) {
        toast(<LoginToast />);
        return;
      }
      if (!isOnline) {
        toast("You must be online to save your progress.");
        return;
      }
      const rows2Mutate = [vocab].filter(_temp);
      if (rows2Mutate.length === 0) {
        return;
      }
      get(userWordPhaseMutationAtom).mutateAsync(rows2Mutate).catch(console.error);
    };
    $[0] = isOnline;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleVocabToggle = useAtomCallback(t0);
  let t1;
  if ($[2] !== rootRef?.current || $[3] !== t) {
    t1 = t2 => {
      const {
        header
      } = t2;
      const isSorted = header.column.getIsSorted();
      const title = t("Word");
      return <TableHeaderCell header={header} className="active:bg-background-active"><Div className="group gap-1.5 pr-1" onClick={e => {
          header.column.getToggleSortingHandler()?.(e);
          requestAnimationFrame(() => rootRef?.current?.scrollTo({
            top: 0
          }));
        }}><Separator orientation="vertical" className="h-5 group-active:h-full group-[:has(:active)+th]/th:h-full" /><HeaderTitle title={title} isSorted={isSorted} className="*:data-title:text-left" /></Div></TableHeaderCell>;
    };
    $[2] = rootRef?.current;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t4 = columnHelper.accessor(_temp2, {
    id: "word",
    filterFn,
    header: t1,
    cell(t2) {
      const {
        row: row_1,
        cell
      } = t2;
      const {
        wordFamily
      } = row_1.original;
      const last = wordFamily.length - 1;
      return <TableDataCell cell={cell} className="py-1"><Fragment>{wordFamily.map((t3, i) => {
            const {
              pathe: w,
              locators: src
            } = t3;
            return <Fragment key={w}><VocabularyWordAction isMuted={i !== 0 || src.length === 0} word={w} />{i < last && <span className="text-neutral-500 dark:text-neutral-400">, </span>}</Fragment>;
          })}</Fragment></TableDataCell>;
    }
  });
  let t5;
  if ($[5] !== rootRef?.current || $[6] !== t) {
    t5 = t6 => {
      const {
        header: header_0
      } = t6;
      const isSorted_0 = header_0.column.getIsSorted();
      const title_0 = t("length");
      return <TableHeaderCell header={header_0} className="w-[.1%] active:bg-background-active"><Div className="group gap-2 pr-1 select-none" onClick={e_0 => {
          header_0.column.getToggleSortingHandler()?.(e_0);
          requestAnimationFrame(() => rootRef?.current?.scrollTo({
            top: 0
          }));
        }}><Separator orientation="vertical" className="h-5 group-active:h-full group-[:has(:active)+th]/th:h-full" /><HeaderTitle title={title_0} isSorted={isSorted_0} className="*:data-title:text-right" /></Div></TableHeaderCell>;
    };
    $[5] = rootRef?.current;
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = columnHelper.accessor(_temp3, {
    id: "word.length",
    header: t5,
    cell: _temp4
  });
  let t7;
  if ($[8] !== rootRef?.current) {
    t7 = t8 => {
      const {
        header: header_1
      } = t8;
      const isSorted_1 = header_1.column.getIsSorted();
      return <TableHeaderCell header={header_1} className="w-[.1%] active:bg-background-active"><Div className="group select-none" onClick={e_1 => {
          header_1.column.getToggleSortingHandler()?.(e_1);
          requestAnimationFrame(() => rootRef?.current?.scrollTo({
            top: 0
          }));
        }}><Separator orientation="vertical" className="h-5 group-active:h-full group-[:has(:active)+th]/th:h-full" /><div className="flex min-w-[30px] grow items-center justify-center"><SortIcon isSorted={isSorted_1} fallback={<svg className="icon-[lucide--check-circle]" />} /></div></Div></TableHeaderCell>;
    };
    $[8] = rootRef?.current;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== handleVocabToggle) {
    t8 = t9 => {
      const {
        row: row_4,
        cell: cell_1
      } = t9;
      return <TableDataCell cell={cell_1}><Div className="justify-center pr-px pl-0.5"><VocabToggle vocab={row_4.original.trackedWord} onToggle={handleVocabToggle} /></Div></TableDataCell>;
    };
    $[10] = handleVocabToggle;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = columnHelper.accessor(_temp5, {
    id: "acquaintedStatus",
    filterFn,
    header: t7,
    cell: t8
  });
  let t10;
  if ($[12] !== rootRef?.current || $[13] !== t) {
    t10 = t11 => {
      const {
        header: header_2
      } = t11;
      const isSorted_2 = header_2.column.getIsSorted();
      const title_1 = t("rank");
      return <TableHeaderCell header={header_2} className="w-[.1%] active:bg-background-active"><Div className="group gap-2 pr-1 select-none" onClick={e_2 => {
          header_2.column.getToggleSortingHandler()?.(e_2);
          requestAnimationFrame(() => rootRef?.current?.scrollTo({
            top: 0
          }));
        }}><Separator orientation="vertical" className="h-5 group-active:h-full group-[:has(:active)+th]/th:h-full" /><HeaderTitle title={title_1} isSorted={isSorted_2} className="*:data-title:text-right" /></Div></TableHeaderCell>;
    };
    $[12] = rootRef?.current;
    $[13] = t;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = columnHelper.accessor(_temp6, {
    id: "rank",
    header: t10,
    cell: _temp7
  });
  let t12;
  if ($[15] !== t11 || $[16] !== t4 || $[17] !== t6 || $[18] !== t9) {
    t12 = [t4, t6, t9, t11];
    $[15] = t11;
    $[16] = t4;
    $[17] = t6;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
}
function _temp7(t0) {
  const {
    cell: cell_2,
    getValue: getValue_1
  } = t0;
  const value_0 = getValue_1();
  return <TableDataCell cell={cell_2}><Div className="justify-center pr-px pl-0.5 tracking-[.03em] tabular-nums">{value_0}</Div></TableDataCell>;
}
function _temp6(row_5) {
  return row_5.trackedWord.rank;
}
function _temp5(row_3) {
  return row_3.trackedWord.learningPhase <= 1 ? row_3.trackedWord.learningPhase : row_3.inertialPhase;
}
function _temp4(t0) {
  const {
    cell: cell_0,
    getValue
  } = t0;
  const value = getValue();
  return <TableDataCell cell={cell_0}><Div className="justify-end pr-[9px] pl-0.5 tracking-[.03em] tabular-nums"><span>{value}</span></Div></TableDataCell>;
}
function _temp3(row_2) {
  return row_2.trackedWord.form.length;
}
function _temp2(row_0) {
  return row_0.trackedWord.form;
}
function _temp(row) {
  return row.form.length <= 32;
}